import { useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { isManager, jobsForClient, plansForClient, recordsForClient } from '../../data/domain';
import { useClient, useJobRecords, useJobs, useRodentPlans } from '../../data/hooks';
import type { Client, Job, JobRecord } from '../../data/types';
import { dateTime, displayDate, plural, shortDate, timeLabel } from '../../lib/format';
import { navigate } from '../../lib/router';
import { deleteEntity } from '../../sync/outbox';
import { Icon } from '../../ui/Icon';
import { useUI } from '../../ui/UIProvider';
import { EmptyState, PanelHeader, View } from '../../ui/View';
import { downloadRecord } from '../records/recordDocument';
import { PhotoThumb } from '../records/RecordViewers';
import { downloadPlanImage, hasPlanImage } from '../rodent/planImage';
import { ClientModal } from './ClientModal';

export function ClientProfileView({ clientId }: { clientId: string }) {
  const user = useUser();
  const client = useClient(clientId);
  const [editing, setEditing] = useState(false);

  if (client === undefined) return <View id="client-profile"><p className="hint">Loading client…</p></View>;
  async function remove() {
    if (!client || !window.confirm(`Delete ${client.name}? This cannot be undone.`)) return;
    await deleteEntity('clients', client.id, `Deleted client ${client.name}`);
    navigate('/clients');
  }

  return (
    <View id="client-profile">
      <button className="back-link" id="backToClients" onClick={() => navigate('/clients')}><Icon name="arrowLeft" size={16} />Clients</button>
      <div id="clientProfilePage">
        {client ? (
          <Profile client={client} actions={isManager(user) && (
            <>
              <button className="btn btn-secondary" id="editClient" onClick={() => setEditing(true)}>Edit client</button>
              <button className="btn btn-danger" id="deleteClient" onClick={remove}>Delete client</button>
            </>
          )} />
        ) : <article className="panel"><PanelHeader title="Client not found" summary="This client record does not exist or was deleted." /></article>}
      </div>
      {editing && client && <ClientModal client={client} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />}
    </View>
  );
}

function Profile({ client, actions }: { client: Client; actions: React.ReactNode }) {
  const ui = useUI();
  const jobs = jobsForClient(client, useJobs()).sort((a, b) => `${b.date}${b.startTime}`.localeCompare(`${a.date}${a.startTime}`));
  const plans = plansForClient(client, useRodentPlans());
  const records = recordsForClient(client, useJobRecords());
  return (
    <div className="stack">
      <article className="panel profile-hero">
        <header className="page-heading">
          <div className="page-heading-text">
            <p className="eyebrow">{client.type} client</p>
            <h1>{client.name}</h1>
            <p className="page-summary">{client.address || 'No address recorded'}</p>
          </div>
          {actions && <div className="page-actions">{actions}</div>}
        </header>
        <dl className="stat-grid">
          <div><dt>Classification</dt><dd>{client.type}{client.highRisk && client.type !== 'High risk' ? ' · high-risk place' : ''}</dd></div>
          <div><dt>Primary contact</dt><dd>{client.contact || 'Not recorded'}</dd></div>
          <div><dt>Phone</dt><dd>{client.phone ? <a href={`tel:${client.phone.replace(/\s+/g, '')}`}>{client.phone}</a> : 'Not recorded'}</dd></div>
          <div><dt>Email</dt><dd>{client.email ? <a href={`mailto:${client.email}`}>{client.email}</a> : 'Not recorded'}</dd></div>
          <div><dt>Saved site plans</dt><dd>{plans.length}</dd></div>
        </dl>
      </article>
      <div className="grid-2">
        <article className="panel">
          <PanelHeader title="Site notes" />
          <p className="panel-body prose">{client.notes || 'No site notes recorded.'}</p>
        </article>
        <article className="panel">
          <PanelHeader title="Rodent plans" summary={plural(plans.length, 'saved plan')} />
          <div className="row-list">
            {plans.length ? plans.map((plan) => (
              <div className="row profile-plan" key={plan.id}>
                <span className="row-text"><b>{plural(plan.stations.length, 'bait station')}</b><small>{plan.address} · Saved {displayDate(plan.savedAt)}</small></span>
                <span className="button-group">
                  {hasPlanImage(plan) && <button className="btn btn-secondary btn-sm snapshot-download" onClick={() => downloadPlanImage(plan)}>Download PNG</button>}
                  <button className="btn btn-secondary btn-sm" onClick={() => { ui.open('rodentPlanRequest', { planId: plan.id, at: Date.now() }); navigate('/rodent-planning'); }}>Open plan</button>
                </span>
              </div>
            )) : <EmptyState>No rodent plans saved for this client.</EmptyState>}
          </div>
        </article>
      </div>
      <div className="grid-2">
        <article className="panel">
          <PanelHeader title="Service history" summary={plural(jobs.length, 'job')} />
          <ul className="row-list scroll-list">
            {jobs.length ? jobs.map((job) => (
              <li key={job.id}>
                <button type="button" className="row row-button history-row" onClick={() => ui.open('jobEditor', { mode: 'edit', jobId: job.id })}>
                  <span className="row-text"><b>{job.detail || job.serviceCode}</b><small>{job.techName} · {displayDate(job.date)}, {timeLabel(job.startTime)}</small></span>
                  {job.cancelled ? <span className="badge badge-neutral">Cancelled</span> : job.completed ? <span className="badge badge-success">Completed</span> : <span className="badge badge-warning">Planned</span>}
                </button>
              </li>
            )) : <EmptyState>No service history recorded.</EmptyState>}
          </ul>
        </article>
        <JobRecordsCard records={records} jobs={jobs} />
      </div>
    </div>
  );
}

interface VisitGroup { key: string; date: string; time: string; service: string; technician: string; records: JobRecord[] }

function JobRecordsCard({ records, jobs }: { records: JobRecord[]; jobs: Job[] }) {
  const ui = useUI();
  const groups = new Map<string, VisitGroup>();
  for (const record of records) {
    const job = jobs.find((item) => item.id === record.jobId);
    const key = record.jobId || `${record.jobDate}|${record.jobTime}|${record.jobService}`;
    if (!groups.has(key)) groups.set(key, { key, date: job?.date || record.jobDate || '', time: job?.startTime || record.jobTime || '', service: job?.serviceCode || record.jobService || 'Service record', technician: job?.techName || record.technician || 'Technician not recorded', records: [] });
    groups.get(key)!.records.push(record);
  }
  const sorted = [...groups.values()].sort((a, b) => `${b.date || '0000-00-00'}T${b.time || '00:00'}`.localeCompare(`${a.date || '0000-00-00'}T${a.time || '00:00'}`));
  return (
    <article className="panel job-records-card">
      <PanelHeader title="Job records" summary="Documents and photo evidence grouped by service visit"><span className="badge badge-neutral">{plural(sorted.length, 'visit')}</span></PanelHeader>
      {sorted.length ? (
        <div className="scroll-list">
          {sorted.map((group) => (
            <section className="record-group" key={group.key}>
              <header className="record-group-header"><b>{shortDate(group.date)}</b><small>{group.service}{group.time ? ` · ${timeLabel(group.time)}` : ''} · {group.technician}</small></header>
              <div className="row-list">
                {group.records.map((record) => (
                  <div className="row record-row" key={record.id}>
                    <span className="row-text">
                      <b>{record.type}</b>
                      <small>{dateTime(record.createdAt)}{record.photos.length ? ` · ${plural(record.photos.length, 'photo')}` : ''}</small>
                      {record.type === 'ARMP' && <span className={`badge ${record.review?.status === 'confirmed' ? 'badge-success' : 'badge-warning'}`}>{record.review?.status === 'confirmed' ? `Reviewed by ${record.review.reviewedBy}` : 'Review required'}</span>}
                    </span>
                    <span className="button-group">
                      {record.photos.map((photo, index) => <PhotoThumb key={index} photo={photo} className="photo-thumb" label={`Open photo ${index + 1} from ${record.type}`} onOpen={(src) => ui.open('photo', { src, name: photo.name })} />)}
                      <button className="btn btn-secondary btn-sm" onClick={() => ui.open('recordDetails', { recordId: record.id })}>View</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => downloadRecord(record)}>Download</button>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : <EmptyState>No job records have been saved for this client.</EmptyState>}
    </article>
  );
}
